import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    useReactTable,
    ColumnDef,
    TableOptions,
    SortingState,
    PaginationState,
    ColumnFiltersState,
    flexRender,
    Column,
    Table,
    getCoreRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues
} from '@tanstack/react-table'
import { useQuery } from 'react-query'
import Skeleton from 'react-loading-skeleton'
import { Icon } from '@iconify/react'
import { useDownloadCsvApiMutation, useGetLicenseSummaryDetailsApiMutation } from '../redux/api/getLicenseDetailsApi'
import { Menu } from '@headlessui/react'
import PaginationElement from './PaginationElement'
import { withOutMdtDateAndTime } from '../utilities/helpers'
import { usePurchaseRequestsListMutation } from '../redux/api/purchaseRequestsListApi'
import { commonLabelName } from '../utilities/commonConstants'
import { licenseTableLabel } from '../utilities/pageConstants'

interface License {
    maintenanceId: string
    productName: string
    licenseId: string
    hostName: string
    activationCode: string
    expiresOn: string
    status: string
}

interface LicensesTableProps {
    defaultData?: License[]
    type?: string
    columnFilters?: ColumnFiltersState
    setColumnFilters?: React.Dispatch<React.SetStateAction<ColumnFiltersState | null>>
    mainTab?: string
    currentTab?: string
    onLoadingChange?: (isLoading: boolean) => void
}

const LicenseTable: React.FC<LicensesTableProps> = ({
    defaultData = [],
    mainTab = licenseTableLabel?.myLicenses,
    currentTab,
    onLoadingChange
}) => {
    const [getLicenseSummaryDetails, { isLoading: summaryLoading }] = useGetLicenseSummaryDetailsApiMutation()
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: currentTab === licenseTableLabel?.purchaseRequests ? 10 : 25
    })
    const [loading, setLoading] = useState(false)
    const [sorting, setSorting] = useState<SortingState>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [status, setStatus] = useState<'summary' | 'details'>('summary')
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null)
    const [totalCount, setTotalCount] = useState<number>(0)
    const menuRef = useRef<HTMLDivElement>(null)
    const pageTopRef = useRef<HTMLTableElement | null>(null)
    const statuses: ('summary' | 'details')[] = ['summary', 'details']
    const [selectedRows, setSelectedRows] = useState<any[]>([])
    const [downloadReportLoadingState, setDownloadReportLoadingState] = useState('')
    const [purchaseRequestsList] = usePurchaseRequestsListMutation()
    const [downloadCsv, { isLoading: downloadReportLoading }] = useDownloadCsvApiMutation()
    const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])
    const handleFetchRequests = async () => {
        try {
            const page = 1
            const limit = 10
            await purchaseRequestsList({ page, limit }).unwrap()
        } catch (err) {
            console.error('Failed to fetch purchase requests:', err)
        }
    }
    const toggleMenu = () => {
        setIsMenuOpen((prevState) => !prevState)
        setActiveSubMenu(null)
    }
    useEffect(() => {
        if (currentTab === 'Profile Details') handleFetchRequests()
    }, [])
    const toggleSubMenu = (submenu: string) => {
        setActiveSubMenu((prev) => (prev === submenu ? null : submenu))
    }

    useEffect(() => {
        setSelectedRows([])
        setSelectAllAcrossPages(false)
        // Optionally, also reset row selection in TanStack Table if needed:
        table.resetRowSelection()
    }, [pagination.pageIndex])

    // Notify parent component about loading state changes
    useEffect(() => {
        const isTableLoading = loading || summaryLoading || downloadReportLoading
        onLoadingChange?.(isTableLoading)
    }, [loading, summaryLoading, downloadReportLoading, onLoadingChange])

    // Define fetchLicenses function inside the component
    const fetchLicenses = async (options: {
        pageIndex: number
        pageSize: number
        sorting: SortingState
        columnFilters: ColumnFiltersState
    }) => {
        const { pageIndex, pageSize, sorting, columnFilters } = options
        const params: Record<string, any> = {}

        const summarySortColumns = sorting.filter((s) => ['maintenanceId', 'product', 'expireDate'].includes(s.id))
        const detailsSortColumns = sorting.filter((s) => ['maintenanceId', 'product', 'hostname', 'type'].includes(s.id))

        if (sorting.length) {
            const sort = status === licenseTableLabel?.summary ? summarySortColumns[0] : detailsSortColumns[0]
            if (sort) {
                params.sort = `${sort.id}:${sort.desc ? 'desc' : 'asc'}`
            }
        }

        if (columnFilters.length) {
            params.filter = columnFilters.reduce((acc: { [key: string]: any }, filter) => {
                acc[filter.id] = filter.value
                return acc
            }, {})
        }

        const payload = {
            page: pageIndex + 1,
            limit: pageSize,
            isSummary: status === licenseTableLabel?.summary,
            ...params
        }

        try {
            setLoading(true)

            if (currentTab === licenseTableLabel?.purchaseRequests) {
                const result = await purchaseRequestsList(payload).unwrap()
                if (result && result?.response && result?.response?.data) {
                    const transformedData = result?.response?.data.map((data: any, index: number) => ({
                        serialNumber: pageIndex * pageSize + index + 1,
                        productName: data?.productName,
                        quantity: data?.quantity,
                        totalPrice: data?.totalPrice,
                        reviewStatus: data?.status
                    }))

                    setTotalCount(result?.response?.totalCount)
                    setLoading(false)

                    return {
                        response: {
                            data: transformedData,
                            total: result?.response?.totalCount
                        }
                    }
                }
            } else {
                const result = await getLicenseSummaryDetails(payload)

                if ('data' in result && result?.data) {
                    const licenses = result?.data?.data?.items
                    const transformLicenseData = (data: any, isSummary: boolean) => {
                        const commonFields = {
                            activationCode: data?.activationCode || '-',
                            licensee: data?.licensee === 'null' ? '-' : data?.licensee || '-'
                        }

                        if (isSummary) {
                            return {
                                ...commonFields,
                                contractType: data?.contractType || '-',
                                expireDate: data?.expireDate || '-',
                                maintenanceId: data?.maintenanceId || '-',
                                product: data?.product || '-',
                                remainingQuantity: data?.remainingQuantity || 0,
                                totalQuantity: data?.totalQuantity || 0,
                                usedQuantity: data?.usedQuantity || 0
                            }
                        } else {
                            return {
                                ...commonFields,
                                maintenanceId: data?.maintenanceId || '-',
                                product: data?.product || '-',
                                licenseNumber: data?.licenseNumber || '-',
                                type: data?.type || '',
                                hostname: data?.hostname || '',
                                count: data?.count || '',
                                expires: data?.expires || ''
                            }
                        }
                    }

                    const transformedData = licenses.map((data: any) => transformLicenseData(data, status === licenseTableLabel?.summary))

                    setTotalCount(result.data.data.total)
                    setLoading(false)
                    console.log(result?.data?.data, 'result?.data?.data')

                    return {
                        response: {
                            data: transformedData,
                            total: result?.data?.data?.total,
                            allMaintenanceIds: result?.data?.data?.allMaintenanceIds
                        }
                    }
                } else if ('error' in result && result?.error) {
                    setLoading(false)
                    console.error('Error fetching licenses data:', result?.error)
                }
            }

            return {
                response: {
                    data: [],
                    total: 0
                }
            }
        } catch (error) {
            setLoading(false)
            console.error('Error fetching licenses data:', error)
            return {
                response: {
                    data: [],
                    total: 0
                }
            }
        }
    }

    const licenseQuery = useQuery(
        ['licenses', pagination, sorting, columnFilters],
        () =>
            fetchLicenses({
                pageIndex: pagination?.pageIndex,
                pageSize: pagination?.pageSize,
                sorting,
                columnFilters
            }),
        {
            keepPreviousData: true,
            refetchOnMount: false,
            refetchOnReconnect: false,
            refetchOnWindowFocus: false
        }
    )

    // Pagination event handlers
    const handlePageChange = (pageNumber: number) => {
        table.setPageIndex(pageNumber - 1)
        pageTopRef.current?.scrollIntoView()
    }

    const handleFirstPage = () => {
        table.setPageIndex(0)
        pageTopRef.current?.scrollIntoView()
    }

    const handleLastPage = () => {
        table.setPageIndex(table.getPageCount() - 1)
        pageTopRef.current?.scrollIntoView()
    }

    const handlePrevPage = () => {
        table.previousPage()
        pageTopRef.current?.scrollIntoView()
    }

    const handleNextPage = () => {
        table.nextPage()
        pageTopRef.current?.scrollIntoView()
    }

    const handleRowSelection = (row: any, isSelected: boolean) => {
        setSelectAllAcrossPages(false) // Unset "select all" if user manually selects/deselects
        setSelectedRows((prev) => {
            const formattedRow = {
                subId: row.original.maintenanceId,
                activationCode: row.original.activationCode,
                licenseNumber: row.original.licenseNumber || ''
            }
            return isSelected
                ? [...prev, formattedRow]
                : prev.filter(
                      (item) =>
                          !(
                              item.subId === row.original.maintenanceId &&
                              item.activationCode === row.original.activationCode &&
                              (item.licenseNumber || '') === (row.original.licenseNumber || '')
                          )
                  )
        })
    }

    const allMaintenanceIds = Array.from(new Set(licenseQuery?.data?.response?.allMaintenanceIds || []))
    const isAllRowsSelected = selectedRows.length === totalCount && totalCount > 0

    const handleExport = async (format: 'selected-pdf' | 'selected-csv' | 'all-pdf' | 'all-csv') => {
        setDownloadReportLoadingState(format)

        const filter = columnFilters?.length
            ? columnFilters.reduce((acc: { [key: string]: any }, filter) => {
                  acc[filter.id] = filter.value
                  return acc
              }, {})
            : undefined

        const payload =
            format === 'all-csv' ||
            format === 'all-pdf' ||
            ((selectAllAcrossPages || isAllRowsSelected) && (format === 'selected-csv' || format === 'selected-pdf'))
                ? {
                      isFetchAll: true,
                      isCsv: format === 'all-csv' || format === 'selected-csv',
                      limit: status !== licenseTableLabel?.summary ? -1 : pagination?.pageSize,
                      offset: pagination?.pageIndex,
                      isSummary: status === licenseTableLabel?.summary,
                      subId: allMaintenanceIds,
                      ...(filter ? { filter } : {})
                  }
                : {
                      subId: Array.from(new Set(selectedRows.map((row) => row.subId))),
                      isCsv: format === 'selected-csv',
                      isFetchAll: false,
                      limit: -1,
                      isSummary: status === licenseTableLabel?.summary,
                      selectedRows:
                          status === licenseTableLabel?.summary
                              ? selectedRows.map((row) => ({
                                    maintenanceId: row.subId,
                                    activationCode: row.activationCode
                                }))
                              : selectedRows.map((row) => ({
                                    maintenanceId: row.subId,
                                    activationCode: row.activationCode,
                                    licenseNumber: row.licenseNumber
                                })),
                      offset: pagination?.pageIndex,
                      ...(filter ? { filter } : {})
                  }

        try {
            const result = await downloadCsv(payload)

            if ('data' in result && result.data && result.data.data) {
                // Extract the presigned URL
                const presignedUrl = result.data.data

                // Validate the presigned URL
                if (!presignedUrl || typeof presignedUrl !== 'string') {
                    console.error('Invalid presigned URL.')
                    return
                }

                // Use GET to check availability
                const response = await fetch(presignedUrl, { method: 'GET' })

                if (response.ok) {
                    const fileName = result.data.fileName && result.data.fileName
                    const blob = await response.blob()
                    const link = document.createElement('a')
                    link.href = URL.createObjectURL(blob)
                    link.download = fileName

                    // Append the link to the document and trigger the download
                    document.body.appendChild(link)
                    link.click()
                    document.body.removeChild(link)
                } else {
                    console.error('File not available at the presigned URL. HTTP Status:', response.status)
                }
            } else if ('error' in result && result.error) {
                console.error('Export failed:', result.error)
            } else {
                console.error('Unexpected result from API.')
            }
        } catch (error) {
            console.error('Export failed:', error)
        }
    }

    const IndeterminateCheckbox = ({
        indeterminate,
        className = '',
        onCheckboxChange,
        ...rest
    }: {
        indeterminate?: boolean
        onCheckboxChange?: (checked: boolean) => void
    } & React.HTMLProps<HTMLInputElement>) => {
        const ref = React.useRef<HTMLInputElement>(null)

        React.useEffect(() => {
            if (ref.current && typeof indeterminate === 'boolean') {
                ref.current.indeterminate = !rest.checked && indeterminate
            }
        }, [indeterminate, rest.checked])

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (onCheckboxChange) {
                onCheckboxChange(e.target.checked)
            }
            if (rest.onChange) {
                rest.onChange(e)
            }
        }

        return <input type='checkbox' ref={ref} className={`${className} cursor-pointer`} {...rest} onChange={handleChange} />
    }

    const columns = useMemo<ColumnDef<License, any>[]>(() => {
        const commonCheckboxClass =
            'w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600'

        const checkboxColumn = {
            id: 'select',
            header: ({ table }: any) => (
                <div className='flex justify-center items-center h-full'>
                    <IndeterminateCheckbox
                        checked={selectAllAcrossPages || table.getIsAllRowsSelected()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const isChecked = e.target.checked
                            setSelectAllAcrossPages(isChecked)
                            if (isChecked) {
                                setSelectedRows([]) // Clear individual selections when selecting all
                            }
                            table.getRowModel().rows.forEach((row: { toggleSelected: (arg0: boolean) => void }) => {
                                handleRowSelection(row, isChecked)
                                row.toggleSelected(isChecked)
                            })
                        }}
                        className={commonCheckboxClass}
                    />
                </div>
            ),
            cell: ({ row }: any) => (
                <div className='flex justify-center items-center h-full'>
                    <IndeterminateCheckbox
                        checked={row.getIsSelected()}
                        indeterminate={row.getIsSomeSelected()}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const isChecked = e.target.checked
                            handleRowSelection(row, isChecked)
                            row.toggleSelected(isChecked)
                        }}
                        className={commonCheckboxClass}
                    />
                </div>
            )
        }
        /**
         * Common properties applied to table columns.
         */
        const commonColumnProperties = {
            /**
             * Determines if the column can be sorted.
             * @type {boolean}
             */
            canSort: false,

            /**
             * Enables or disables sorting for the column.
             * @type {boolean}
             */
            enableSorting: false,

            /**
             * Enables or disables column filtering.
             * @type {boolean}
             */
            enableColumnFilter: false,

            /**
             * Function that retrieves the cell value.
             * @param {object} info - The cell information object.
             * @param {Function} info.getValue - Function to get the cell value.
             * @returns {*} The value of the cell.
             */
            cell: (info: { getValue: () => any }) => info.getValue()
        }

        /**
         * Configuration for columns in the summary license table.
         * Each column includes properties for sorting, filtering, and rendering custom cells.
         */
        const summaryLicenseColumns = [
            /**
             * Column for Maintenance ID, sortable and filterable.
             */
            { header: 'Original Subscription', accessorKey: 'maintenanceId', canSort: true, enableSorting: true, enableColumnFilter: true },

            /**
             * Column for Company / Institution Name.
             */
            { header: 'Company / Institution Name', accessorKey: 'licensee', canSort: true, enableColumnFilter: true },

            /**
             * Column for Product with filtering enabled.
             */
            { header: 'Product', accessorKey: 'product', enableColumnFilter: true },

            /**
             * Column for Activation Code, displayed within a styled `<span>`.
             */
            {
                header: 'Activation Code',
                accessorKey: 'activationCode',
                enableColumnFilter: true,
                /**
                 * Renders the activation code inside a span to ensure proper formatting.
                 * @param {object} info - Cell information object.
                 * @param {Function} info.getValue - Function to get the cell value.
                 * @returns {JSX.Element} A span element with the activation code.
                 */
                cell: (info: { getValue: () => any }) => <span className='w-full text-nowrap'>{info.getValue()}</span>
            },

            /**
             * Column for Total Quantity.
             */
            { header: 'Total Quantity', accessorKey: 'totalQuantity' },

            /**
             * Column for Used Quantity.
             */
            { header: 'Used Quantity', accessorKey: 'usedQuantity' },

            /**
             * Column for Remaining Quantity.
             */
            { header: 'Remaining Quantity', accessorKey: 'remainingQuantity' },

            /**
             * Column for Expiration Date, sortable.
             */
            { header: 'Expiration Date', accessorKey: 'expireDate', canSort: true, enableSorting: true },

            /**
             * Column for Contract Type, with conditional styling for different contract types.
             */
            {
                header: 'Contract Type',
                accessorKey: 'contractType',
                /**
                 * Renders the contract type inside a styled `<span>` with conditional colors.
                 * @param {object} info - Cell information object.
                 * @param {Function} info.getValue - Function to get the cell value.
                 * @returns {JSX.Element} A styled span indicating contract type.
                 */
                cell: (info: { getValue: () => any }) => (
                    <span
                        className={`px-3 py-1 rounded-md font-semibold text-sm ${
                            info.getValue() === 'Perpetual' ? 'bg-sky text-primary' : 'bg-red-800 text-red-200'
                        }`}
                    >
                        {info.getValue()}
                    </span>
                )
            }
        ].map((col) => ({ ...commonColumnProperties, ...col }))

        const licensesDetailsColumns = [
            { header: 'Original Subscription', accessorKey: 'maintenanceId', enableColumnFilter: true },
            {
                header: 'Company / Institution Name',
                accessorKey: 'licensee',
                canSort: true,
                enableColumnFilter: true
            },
            { header: 'Product', accessorKey: 'product', enableColumnFilter: true },
            { header: 'License #', accessorKey: 'licenseNumber', enableColumnFilter: true },
            { header: 'Host Name', accessorKey: 'hostname', canSort: true, enableColumnFilter: true, enableSorting: true },
            { header: 'Type', accessorKey: 'type', canSort: true, enableSorting: true },
            {
                header: 'Activation Code(s)',
                accessorKey: 'activationCode',
                enableColumnFilter: true,
                cell: (info: { getValue: () => any }) => <span className={`w-full text-nowrap`}>{info.getValue()}</span>
            },
            { header: 'Count', accessorKey: 'count' },
            {
                header: 'Expires',
                accessorKey: 'expires',
                cell: (info: { getValue: () => any }) => (
                    <span
                        className={`px-3 py-1 rounded-md ${
                            info.getValue() === 'Perpetual' && 'bg-sky text-primary font-bold'
                        } font-normal text-md w-full text-nowrap`}
                    >
                        {info.getValue()}
                    </span>
                )
            }
        ].map((col) => ({ ...commonColumnProperties, ...col }))

        const licenseRequestsColumns = [
            {
                header: 'S.No',
                accessorKey: 'serialNumber',
                cell: (info: { getValue: () => any }) => (
                    <span className={`px-3 py-1 rounded-md font-normal text-sm`}>{info.getValue()}</span>
                )
            },
            { header: 'Product', accessorKey: 'product' },
            { header: 'File Name', accessorKey: 'fileName' },
            { header: 'Uploaded Date & Time', accessorKey: 'uploadedDateTime' },
            { header: 'Review Status', accessorKey: 'reviewStatus' }
        ].map((col) => ({ ...commonColumnProperties, ...col }))

        const purchaseRequestsColumns = [
            {
                header: 'S.No',
                accessorKey: 'serialNumber',
                cell: (info: { getValue: () => any }) => (
                    <span className={`px-3 py-1 rounded-md font-normal text-sm`}>{info.getValue()}</span>
                )
            },
            {
                header: 'Product',
                accessorKey: 'productName',
                cell: (info: { getValue: () => any }) => (
                    <span className={`px-3 py-1 rounded-md font-normal text-sm`}>{info.getValue()}</span>
                )
            },
            {
                header: 'Quantity',
                accessorKey: 'quantity',
                cell: (info: { getValue: () => any }) => {
                    const value = info.getValue()
                    const formattedValue = String(value).padStart(2, '0')
                    return <span className='px-3 py-1 rounded-md font-normal text-sm'>{formattedValue}</span>
                }
            },
            {
                header: 'Price',
                accessorKey: 'totalPrice',
                cell: (info: { getValue: () => any }) => (
                    <span className={`px-3 py-1 rounded-md font-normal text-sm`}>{info.getValue()}</span>
                )
            },
            { header: 'Review Status', accessorKey: 'reviewStatus' }
        ].map((col) => ({ ...commonColumnProperties, ...col }))

        if (currentTab === licenseTableLabel?.purchaseRequests) {
            return purchaseRequestsColumns
        }

        if (currentTab === licenseTableLabel?.licenseRequests) {
            return licenseRequestsColumns
        }

        return status === licenseTableLabel?.summary
            ? [checkboxColumn, ...summaryLicenseColumns]
            : [checkboxColumn, ...licensesDetailsColumns]
    }, [status, currentTab])

    useEffect(() => {
        setSelectedRows([])
        setColumnFilters([])
        table.resetRowSelection()
        // eslint-disable-next-line
    }, [status])

    useEffect(() => {
        setPagination((prev) => ({
            ...prev,
            pageIndex: 0,
            pageSize: 25 // Reset to default page size when switching tabs
        }))
        // Reset row selection when filters change
        setSelectedRows([])
        setSelectAllAcrossPages(false)
        table.resetRowSelection()
    }, [sorting, columnFilters, status])

    useEffect(() => {
        licenseQuery.refetch()
        // eslint-disable-next-line
    }, [status, sorting, columnFilters])

    const table = useReactTable({
        data: licenseQuery?.data?.response?.data || defaultData,
        columns,
        state: {
            sorting,
            columnFilters,
            pagination
        },
        pageCount: Math.ceil((licenseQuery?.data?.response?.total ?? 0) / pagination.pageSize),
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues()
    } as TableOptions<any>)

    if (licenseQuery.isError) {
        return <div>{licenseTableLabel?.errorLoading}</div>
    }
    return (
        <>
            {mainTab === licenseTableLabel?.myLicenses &&
                currentTab !== licenseTableLabel?.licenseRequests &&
                currentTab !== licenseTableLabel?.purchaseRequests && (
                    <>
                        <div className='font-normal text-primary text-center italic mb-6 p-3 rounded bg-sky border-primary'>
                            <p>
                                <strong>License Administrators (and Distributors)</strong> can view license Original Subscription # details
                                and usage (must be
                                <span className='underline'> registered in your name </span> and
                                <span className='underline'> current on maintenance</span>). For more details, please see the{' '}
                                <a
                                    href='https://www.nv5geospatialsoftware.com/Support/Self-Help-Tools/Help-Articles/Help-Articles-Detail/my-licenses-portal-users-guide'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='text-primary underline'
                                >
                                    My Licenses Users Guide
                                </a>
                                :
                            </p>

                            <ul className='list-disc mt-2 text-left inline-block'>
                                <li>
                                    <span className='font-semibold'>Summary tab:</span> Type and number of licenses available to you.
                                </li>
                                <li>
                                    <span className='font-semibold'>Details tab:</span> Licenses already activated to your devices.
                                </li>
                            </ul>
                        </div>
                        <div className='flex items-center justify-between mb-4'>
                            <div className='flex items-center mb-4 border border-lightGray rounded-lg w-fit'>
                                {statuses.map((statusValue) => (
                                    <button
                                        key={statusValue}
                                        onClick={() => setStatus(statusValue)}
                                        disabled={licenseQuery.isLoading || summaryLoading || loading}
                                        className={`px-10 py-2 font-semibold ${
                                            licenseQuery.isLoading || summaryLoading || loading ? 'cursor-not-allowed' : ''
                                        } ${statusValue !== 'details' && 'border-r'} ${
                                            status === statusValue ? 'bg-lightBlue text-blue' : 'text-grey-400'
                                        } ${
                                            statusValue === licenseTableLabel?.summary
                                                ? 'rounded-l-lg'
                                                : statusValue === 'details' && 'rounded-r-lg'
                                        }`}
                                    >
                                        {statusValue.charAt(0).toUpperCase() + statusValue.slice(1)}
                                    </button>
                                ))}
                            </div>

                            <Menu as='div' className='relative' ref={menuRef}>
                                <Menu.Button className='relative flex px-[8px] py-[5px] items-center' onClick={toggleMenu}>
                                    <div
                                        className={`flex items-center gap-2 text-md font-medium text-white bg-primary px-4 py-2 rounded-lg ${
                                            licenseQuery.isLoading || summaryLoading || loading ? 'cursor-not-allowed opacity-50' : ''
                                        }`}
                                        onClick={(e) => {
                                            if (licenseQuery.isLoading || summaryLoading || loading) {
                                                e.preventDefault()
                                            }
                                        }}
                                    >
                                        {commonLabelName?.export}
                                        <Icon
                                            icon={`prime:chevron-${isMenuOpen ? 'up' : 'down'}`}
                                            className='text-white'
                                            width='24'
                                            height='24'
                                        />
                                    </div>
                                </Menu.Button>
                                {isMenuOpen && (
                                    <Menu.Items
                                        className='absolute right-0 z-10 shadow-lg origin-top-right rounded-lg bg-white py-1 focus:outline-none'
                                        style={{ width: '200px' }}
                                    >
                                        {/* Export Selected */}
                                        <div>
                                            <div
                                                className={`flex items-center justify-between px-4 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100 rounded-t-lg ${
                                                    downloadReportLoading ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
                                                }`}
                                                onClick={() => !downloadReportLoading && toggleSubMenu('selected')}
                                            >
                                                {licenseTableLabel?.exportSelect}
                                                <Icon
                                                    icon={`prime:chevron-${activeSubMenu === 'selected' ? 'up' : 'down'}`}
                                                    className='text-gray-700'
                                                    width='16'
                                                    height='16'
                                                />
                                            </div>
                                            {activeSubMenu === 'selected' && (
                                                <div className='pl-6 pr-4 py-2'>
                                                    <div
                                                        className={`flex items-center gap-2 px-2 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100 rounded-lg ${
                                                            selectedRows.length === 0 ||
                                                            (downloadReportLoading && downloadReportLoadingState !== 'selected-pdf')
                                                                ? 'opacity-50 cursor-not-allowed pointer-events-none'
                                                                : ''
                                                        }`}
                                                        onClick={() => !downloadReportLoading && handleExport('selected-pdf')}
                                                    >
                                                        {commonLabelName?.exportAsPdf}
                                                        {downloadReportLoading && downloadReportLoadingState === 'selected-pdf' && (
                                                            <div className='ml-3 border-2 border-[#006298] rounded-full h-3 w-3 animate-spin border-t-white' />
                                                        )}
                                                    </div>
                                                    <div
                                                        className={`flex items-center gap-2 px-2 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100 rounded-lg ${
                                                            selectedRows.length === 0 ||
                                                            (downloadReportLoading && downloadReportLoadingState !== 'selected-csv')
                                                                ? 'opacity-50 cursor-not-allowed pointer-events-none'
                                                                : ''
                                                        }`}
                                                        onClick={() => !downloadReportLoading && handleExport('selected-csv')}
                                                    >
                                                        {commonLabelName?.exportAsCsv}
                                                        {downloadReportLoading && downloadReportLoadingState === 'selected-csv' && (
                                                            <div className='ml-3 border-2 border-[#006298] rounded-full h-3 w-3 animate-spin border-t-white' />
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <hr className='border-gray' />
                                        {/* Export All */}
                                        <div>
                                            <div
                                                className={`flex items-center justify-between px-4 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100 rounded-t-lg ${
                                                    downloadReportLoading ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''
                                                }`}
                                                onClick={() => !downloadReportLoading && toggleSubMenu('all')}
                                            >
                                                {commonLabelName?.exportAll}
                                                <Icon
                                                    icon={`prime:chevron-${activeSubMenu === 'all' ? 'up' : 'down'}`}
                                                    className='text-gray-700'
                                                    width='16'
                                                    height='16'
                                                />
                                            </div>
                                            {activeSubMenu === 'all' && (
                                                <div className='pl-6 pr-4 py-2'>
                                                    <div
                                                        className={`flex items-center gap-2 px-2 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100 rounded-lg ${
                                                            downloadReportLoading && downloadReportLoadingState !== 'all-pdf'
                                                                ? 'opacity-50 cursor-not-allowed pointer-events-none'
                                                                : ''
                                                        }`}
                                                        onClick={() => !downloadReportLoading && handleExport('all-pdf')}
                                                    >
                                                        {commonLabelName?.exportAsPdf}
                                                        {downloadReportLoading && downloadReportLoadingState === 'all-pdf' && (
                                                            <div className='ml-3 border-2 border-[#006298] rounded-full h-3 w-3 animate-spin border-t-white' />
                                                        )}
                                                    </div>
                                                    <div
                                                        className={`flex items-center gap-2 px-2 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100 rounded-lg ${
                                                            downloadReportLoading && downloadReportLoadingState !== 'all-csv'
                                                                ? 'opacity-50 cursor-not-allowed pointer-events-none'
                                                                : ''
                                                        }`}
                                                        onClick={() => !downloadReportLoading && handleExport('all-csv')}
                                                    >
                                                        {commonLabelName?.exportAsCsv}
                                                        {downloadReportLoading && downloadReportLoadingState === 'all-csv' && (
                                                            <div className='ml-3 border-2 border-[#006298] rounded-full h-3 w-3 animate-spin border-t-white' />
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Menu.Items>
                                )}
                            </Menu>
                        </div>
                    </>
                )}
            {mainTab === licenseTableLabel?.myLicenses && currentTab !== licenseTableLabel?.licenseRequests && (
                <div className=' mx-auto py-8' ref={pageTopRef}>
                    <div className='bg-white p-4 rounded-lg shadow-lg'>
                        <div className='border border-gray rounded-lg p-4'>
                            <div className='col-span-12'>
                                <div className='relative overflow-x-auto table-scroll' style={{ maxHeight: '500px' }}>
                                    <table className='w-full overflow-auto'>
                                        <thead className='sticky top-0 bg-white'>
                                            {table.getHeaderGroups().map((headerGroup) => (
                                                <tr key={headerGroup?.id} className='align-baseline text-left'>
                                                    {headerGroup.headers.map((header) => (
                                                        <th
                                                            key={header?.id}
                                                            colSpan={header?.colSpan}
                                                            className={`${
                                                                mainTab === licenseTableLabel?.myLicenses && 'text-secondary'
                                                            } text-primary p-3 pt-0`}
                                                            style={{
                                                                width: header.column.getSize(),
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            {header.isPlaceholder ? null : (
                                                                <>
                                                                    <div
                                                                        {...{
                                                                            className: header.column.getCanSort()
                                                                                ? 'cursor-pointer select-none flex items-center w-[100%]'
                                                                                : '',
                                                                            onClick: header.column.getCanSort()
                                                                                ? header.column.getToggleSortingHandler()
                                                                                : undefined
                                                                        }}
                                                                    >
                                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                                        {header.column.getCanSort() ? (
                                                                            <div>
                                                                                <Icon icon='akar-icons:sort' className='mx-4' />
                                                                            </div>
                                                                        ) : null}
                                                                    </div>
                                                                    {header.column.getCanFilter() ? (
                                                                        <div>
                                                                            <Filter column={header.column} table={table} />
                                                                        </div>
                                                                    ) : null}
                                                                </>
                                                            )}
                                                        </th>
                                                    ))}
                                                </tr>
                                            ))}
                                        </thead>

                                        {licenseQuery.isLoading || summaryLoading || loading ? (
                                            <tbody>
                                                <tr>
                                                    {Array.from({
                                                        length: currentTab === licenseTableLabel?.purchaseRequests ? 5 : 10
                                                    }).map((_, index) => (
                                                        <td key={index} className='p-3'>
                                                            <Skeleton count={3} />
                                                        </td>
                                                    ))}
                                                </tr>
                                            </tbody>
                                        ) : (
                                            <tbody className='text-left border-t border-gray text-secondary font-medium text-md'>
                                                {licenseQuery?.data?.response?.data?.length ? (
                                                    <>
                                                        {table.getRowModel().rows.map((row, index) => (
                                                            <tr
                                                                key={row.id}
                                                                className={`p-3 ${
                                                                    index !== table.getRowModel().rows.length - 1 && 'border-b border-gray'
                                                                }`}
                                                                style={{
                                                                    backgroundColor: index % 2 === 0 ? 'rgb(248, 251, 250)' : 'white'
                                                                }}
                                                            >
                                                                {row.getVisibleCells().map((cell) => {
                                                                    return (
                                                                        <td
                                                                            key={cell.id}
                                                                            className={`p-3 ${
                                                                                currentTab === licenseTableLabel?.purchaseRequests &&
                                                                                cell.column.id === 'reviewStatus' &&
                                                                                'px-2 py-1 text-xs font-medium rounded-md'
                                                                            }`}
                                                                            style={
                                                                                currentTab === licenseTableLabel?.purchaseRequests &&
                                                                                cell.column.id === 'reviewStatus'
                                                                                    ? {}
                                                                                    : {}
                                                                            }
                                                                        >
                                                                            {(() => {
                                                                                const cellValue = cell.getValue()
                                                                                // Check if it's the Product column and the value is an error object
                                                                                if (
                                                                                    cell.column.id === 'product' &&
                                                                                    typeof cellValue === 'object' &&
                                                                                    (cellValue as any)?.Error
                                                                                ) {
                                                                                    return (
                                                                                        <span
                                                                                            title={(cellValue as any).Error}
                                                                                            style={{ cursor: 'help' }}
                                                                                        >
                                                                                            -
                                                                                        </span>
                                                                                    )
                                                                                }
                                                                                if (
                                                                                    currentTab === licenseTableLabel?.purchaseRequests &&
                                                                                    cell.column.id === 'reviewStatus'
                                                                                ) {
                                                                                    return (
                                                                                        <span
                                                                                            className='px-2 py-1 text-xs font-semibold rounded-md'
                                                                                            style={{
                                                                                                backgroundColor:
                                                                                                    cell.getValue() === 'ACCEPTED'
                                                                                                        ? '#eef5eb'
                                                                                                        : cell.getValue() === 'PENDING'
                                                                                                          ? '#fcf7e9'
                                                                                                          : cell.getValue() === 'DECLINED'
                                                                                                            ? '#f4e6e6'
                                                                                                            : 'transparent',
                                                                                                color:
                                                                                                    cell.getValue() === 'ACCEPTED'
                                                                                                        ? '#4caf50'
                                                                                                        : cell.getValue() === 'PENDING'
                                                                                                          ? '#e3b026'
                                                                                                          : cell.getValue() === 'DECLINED'
                                                                                                            ? '#b45151'
                                                                                                            : 'inherit'
                                                                                            }}
                                                                                        >
                                                                                            {cell.getValue() === 'ACCEPTED'
                                                                                                ? 'Accepted'
                                                                                                : cell.getValue() === 'PENDING'
                                                                                                  ? 'Pending'
                                                                                                  : cell.getValue() === 'DECLINED' &&
                                                                                                    'Declined'}
                                                                                        </span>
                                                                                    )
                                                                                }
                                                                                return flexRender(
                                                                                    cell.column.columnDef.cell,
                                                                                    cell.getContext()
                                                                                )
                                                                            })()}
                                                                        </td>
                                                                    )
                                                                })}
                                                            </tr>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <tr>
                                                        <td colSpan={table.getVisibleLeafColumns().length} className='p-3 text-center'>
                                                            {commonLabelName?.noData}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        )}
                                    </table>
                                </div>
                            </div>
                        </div>
                        {licenseQuery.data?.response?.data?.length && !licenseQuery.isLoading && !summaryLoading && !loading ? (
                            <div className='flex items-center justify-between gap-2 mt-2 px-4 py-3 font-semibold'>
                                <div className='flex items-center gap-4'>
                                    <div className='text-sm text-[#2D3748]'>
                                        Showing{' '}
                                        {Math.min(
                                            table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1,
                                            totalCount
                                        )}{' '}
                                        to{' '}
                                        {Math.min(
                                            (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                            totalCount
                                        )}{' '}
                                        of {totalCount} entries
                                    </div>
                                    <select
                                        value={pagination.pageSize}
                                        onChange={(e) => {
                                            const newPageSize = Number(e.target.value)
                                            setPagination((prev) => ({
                                                ...prev,
                                                pageSize: newPageSize,
                                                pageIndex: 0 // Reset to first page when changing page size
                                            }))
                                        }}
                                        className='border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                                    >
                                        <option value={25}>25</option>
                                        <option value={100}>100</option>
                                        <option value={200}>200</option>
                                    </select>
                                </div>
                                <div className='flex'>
                                    <PaginationElement
                                        currentPage={table.getState().pagination.pageIndex + 1}
                                        totalPages={table.getPageCount()}
                                        onPageChange={handlePageChange}
                                        onFirstPage={handleFirstPage}
                                        onLastPage={handleLastPage}
                                        onPrevPage={handlePrevPage}
                                        onNextPage={handleNextPage}
                                    />
                                </div>
                            </div>
                        ) : (
                            ''
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

function Filter({ column, table }: { column: Column<any, unknown>; table: Table<any> }) {
    const firstValue = table.getPreFilteredRowModel().flatRows[0]?.getValue(column.id)

    const columnFilterValue = column.getFilterValue()

    const sortedUniqueValues = useMemo(
        () => (typeof firstValue === 'number' ? [] : Array.from(column.getFacetedUniqueValues().keys()).sort()),
        // eslint-disable-next-line
        [column.getFacetedUniqueValues()]
    )

    return typeof firstValue === 'number' ? (
        <div>
            <div className='flex space-x-2'>
                <DebouncedInput
                    type='number'
                    min={Number(column.getFacetedMinMaxValues()?.[0] ?? '')}
                    max={Number(column.getFacetedMinMaxValues()?.[1] ?? '')}
                    value={(columnFilterValue as [number, number])?.[0] ?? ''}
                    onChange={(value) => column.setFilterValue((old: [number, number]) => [value, old?.[1]])}
                    placeholder={`Min ${column.getFacetedMinMaxValues()?.[0] ? `(${column.getFacetedMinMaxValues()?.[0]})` : ''}`}
                    style={{ maxWidth: column.getSize() }}
                    className='border border-light shadow-sm text-black text-xs rounded-md w-[100%] pl-9 px-2 py-2 font-normal placeholder:text-light focus:outline-none'
                />
            </div>
            <div className='h-1' />
        </div>
    ) : (
        <>
            <datalist id={column.id + 'list'}>
                {sortedUniqueValues.slice(0, 5000).map((value: any) => (
                    <option value={value} key={value} />
                ))}
            </datalist>
            <div className='relative flex items-center mt-2'>
                <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
                    <Icon icon='iconamoon:search' className='text-light w-4 h-4' />
                </div>
                <DebouncedInput
                    type='text'
                    value={(columnFilterValue ?? '') as string}
                    onChange={(value) => column.setFilterValue(value)}
                    placeholder={`Search`}
                    className='border border-light text-black text-xs rounded-md w-[100%] pl-9 px-2 py-2 font-normal placeholder:text-light focus:outline-none'
                    // style={{ maxWidth: column.getSize() }}
                    // list={column.id + 'list'}
                />
            </div>
            <div className='h-1' />
        </>
    )
}

function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: string | number
    onChange: (value: string | number) => void
    debounce?: number
} & React.InputHTMLAttributes<HTMLInputElement>) {
    const [value, setValue] = useState(initialValue)

    useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
        // eslint-disable-next-line
    }, [value])

    return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}

export default LicenseTable
